import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useCreateJournalApprovalRequests } from "src/hooks/journal-approval/useCreateJournalApprovalRequest";
import { useJournalApprovalLookup } from "src/hooks/journal-approval/useJournalApprovalLookup";
import { JournalApprovalLookupItem } from "models/journal-approval";
import throttle from "lodash/throttle";
import { displayJournalType, displayNsStatusName } from "src/util/helpers";

type PreviewRow = JournalApprovalLookupItem & {
  rowId: string;
  status: string;
};

const JournalApprovalForm: React.FC = () => {
  const [idsInput, setIdsInput] = useState<string>("");
  const [ids, setIds] = useState<string[]>([]);
  const [rowData, setRowData] = useState<PreviewRow[]>([]);
  const [isCreateRequestComplete, setIsCreateRequestComplete] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const { items, loading: lookupLoading } = useJournalApprovalLookup(ids);
  const {
    creationResults,
    loading: creationLoading,
    createJournalApprovalRequests,
  } = useCreateJournalApprovalRequests();

  useEffect(() => {
    setRowData(
      items.map((item, index) => ({
        ...item,
        rowId: `${item.inputNumber}-${index}`,
        status: "",
      }))
    );
  }, [items]);

  useEffect(() => {
    if (creationResults.length > 0) {
      setRowData((current) =>
        current.map((row, index) => {
          const result = creationResults[index];
          return result
            ? { ...row, status: result.status }
            : { ...row, status: "UNKNOWN" };
        })
      );
      setIsCreateRequestComplete(true);
    }
  }, [creationResults]);

  const handleLookup = () => {
    const numbers = idsInput
      .split(/[ ,]+/)
      .map((id) => id.trim())
      .filter(Boolean);
    setIds(numbers);
  };

  const handleCreateRequest = throttle(async () => {
    const numbers = idsInput
      .split(/[ ,]+/)
      .map((id) => id.trim())
      .filter(Boolean);
    await createJournalApprovalRequests(
      rowData.length
        ? rowData.map((row) => ({
            journalNumber: row.inputNumber,
            ...(row.journalId ? { journalId: row.journalId } : {}),
            ...(row.journalTranId ? { journalTranId: row.journalTranId } : {}),
            ...(row.recordType ? { recordType: row.recordType } : {}),
            ...(row.recordTypeLabel
              ? { recordTypeLabel: row.recordTypeLabel }
              : {}),
          }))
        : numbers.length
          ? numbers
          : ids
    );
  }, 3000);

  const columns: GridColDef[] = [
    { field: "inputNumber", headerName: "Input", width: 120 },
    { field: "journalId", headerName: "NS ID", width: 120 },
    { field: "journalTranId", headerName: "Number", width: 120 },
    {
      field: "recordTypeLabel",
      headerName: "Type",
      width: 90,
      valueGetter: (_value, row) =>
        displayJournalType(row.recordType, row.recordTypeLabel),
    },
    {
      field: "nsStatusName",
      headerName: "NS Status",
      flex: 1,
      minWidth: 160,
      valueGetter: (value?: string) => displayNsStatusName(value),
    },
    { field: "expected", headerName: "Expected", width: 200, minWidth: 190 },
    { field: "status", headerName: "Create Status", width: 130 },
  ];

  return (
    <Container maxWidth="xl" sx={{ display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mt: 3,
          mb: 3,
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "90vw",
        }}
      >
        <Typography variant="h5" component="h1" align="center">
          Create Journal Approval Request
        </Typography>

        <TextField
          label="Journal numbers (space or comma separated internal ids or numbers)"
          variant="outlined"
          fullWidth
          value={idsInput}
          onChange={(event) => setIdsInput(event.target.value)}
          onBlur={handleLookup}
        />

        <Box sx={{ mt: 2, height: 400, width: "100%" }}>
          <DataGrid
            rows={rowData}
            columns={columns}
            getRowId={(row) => row.rowId}
            disableRowSelectionOnClick
            loading={lookupLoading}
          />
        </Box>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={
              isCreateRequestComplete
                ? () => navigate("/journal-approvals")
                : handleCreateRequest
            }
            disabled={lookupLoading || creationLoading || ids.length === 0}
          >
            {creationLoading ? (
              <CircularProgress size={24} />
            ) : isCreateRequestComplete ? (
              "Return To Menu"
            ) : (
              "Create Journal Approval Request"
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default JournalApprovalForm;
